import React from 'react';
import { Inbox } from 'lucide-react';
import { OutreachAction } from './types';
import StagedTag from './StagedTag';
import { useReadOnly } from '../../../contexts/WizardAccessContext';

/**
 * One of Robbie's moves in full — title, reasoning, working, and its own two
 * controls. The same card wherever a move is shown at full size: over the
 * conversation in the moves sheet, and in the body of a thread nobody has
 * written to yet.
 *
 * Nothing on it reaches a supplier. "Put in the box" writes the ask into the
 * buyer's composer and stops there; the composer's own send is the only way an
 * email leaves.
 *
 * A viewer gets the full reasoning and none of the controls.
 *
 * On a phone the two controls drop below the ask instead of sitting beside it.
 * They are ~190px of fixed width against a 345px card, which left the title
 * about a word per line — and the title is the ask itself, the thing being
 * decided. Underneath, they are full-size targets and the ask reads as a
 * sentence (owner, 18 Sep 2026).
 */
interface ActionCardProps {
  action: OutreachAction;
  /** Its ask is already sitting in the composer, waiting on the buyer's send. */
  staged: boolean;
  disabled?: boolean;
  onStage: () => void;
  onDismiss: () => void;
}

const ActionCard: React.FC<ActionCardProps> = ({ action, staged, disabled, onStage, onDismiss }) => {
  const readOnly = useReadOnly();

  return (
  <div className={`rounded-card border px-[12px] py-s3 ${staged ? 'border-line bg-canvas' : 'border-robbie-border bg-surface'}`}>
    <div className="flex items-start gap-s3 max-md:flex-col">
      <div className="min-w-0 flex-1">
        <div className={`text-[12.5px] font-bold ${staged ? 'text-ink-secondary' : 'text-ink'}`}>{action.title}</div>
        {action.rationale && (
          <p className={`m-0 mt-s1 text-meta leading-[1.45] ${staged ? 'text-ink-muted' : 'text-ink-secondary'}`}>
            {action.rationale}
          </p>
        )}
        {action.anchor?.working && (
          <p className="m-0 mt-s1 text-[10.5px] leading-[1.45] text-ink-muted">{action.anchor.working}</p>
        )}
      </div>
      {readOnly ? null : staged ? (
        <StagedTag />
      ) : (
        <div className="flex shrink-0 items-center gap-s3 max-md:w-full max-md:pt-s2">
          <button
            type="button"
            onClick={onStage}
            disabled={disabled}
            title="I write this ask into your box — edit it, then send when you're happy. Nothing goes out until you do."
            className="inline-flex items-center gap-s2 rounded-control border border-action-border bg-surface px-[11px] py-[5px] text-meta font-semibold text-action-deep hover:bg-action-bg disabled:pointer-events-none disabled:opacity-50"
          >
            <Inbox className="h-3 w-3" /> Put in the box
          </button>
          <button
            type="button"
            onClick={onDismiss}
            disabled={disabled}
            className="text-meta font-semibold text-ink-secondary hover:text-ink disabled:opacity-50"
          >
            Not now
          </button>
        </div>
      )}
    </div>
  </div>
  );
};

export default ActionCard;
